import { actions as dataFetchActions } from '@buffer-mono/async-data-fetch'
import type { Middleware } from '@reduxjs/toolkit'

import type { AppDispatch, RootState } from '~publish/legacy/store'
import { isLegacyAction } from '~publish/legacy/store/isAction'

import { fetchLegacyOrganizations } from './getLegacyOrganizations'
import { actionTypes } from './reducer'

// Monotonic id so a stale INIT_ORGANIZATIONS response cannot overwrite a newer one.
let organizationsLoadGeneration = 0

const middleware: Middleware<object, RootState, AppDispatch> =
  ({ dispatch, getState }) =>
  (next) =>
  (action): void => {
    next(action)
    if (!isLegacyAction(action)) return
    switch (action.type) {
      case 'INIT_ORGANIZATIONS': {
        if (
          typeof window !== 'undefined' &&
          typeof window.bufferData !== 'undefined' &&
          typeof window.bufferData.organizations !== 'undefined' &&
          window.bufferData.organizations
        ) {
          dispatch(
            dataFetchActions.fetchSuccess({
              name: 'organizations',
              result: [...window.bufferData.organizations],
            }),
          )
          // make sure we only bootstrap this data once
          window.bufferData.organizations = undefined
        } else {
          // Billing can re-dispatch INIT_ORGANIZATIONS while a prior load is
          // in flight. Only the latest invocation may write Redux.
          const loadId = ++organizationsLoadGeneration
          void (async (): Promise<void> => {
            try {
              const organizations = await fetchLegacyOrganizations()
              if (loadId !== organizationsLoadGeneration) return
              dispatch(
                dataFetchActions.fetchSuccess({
                  name: 'organizations',
                  result: organizations,
                }),
              )
            } catch (error: unknown) {
              if (loadId !== organizationsLoadGeneration) return
              // async-data-fetch stringifies RPC errors; maintenance-redirect
              // middleware calls error.includes(...). Keep that contract.
              const message =
                error instanceof Error
                  ? error.message
                  : typeof error === 'string'
                    ? error
                    : String(error)
              dispatch(
                dataFetchActions.fetchFail({
                  name: 'organizations',
                  error: message,
                }),
              )
            }
          })()
        }
        break
      }
      case actionTypes.SET_CURRENT_ORGANIZATION: {
        const { list = [], selected } = getState().organizations
        const { organizationId } = action
        if (selected?.id !== organizationId) {
          const selectedOrg = list?.find((org) => org.id === organizationId)
          // Select the org
          dispatch({
            type: actionTypes.ORGANIZATION_SELECTED,
            selected: selectedOrg,
          })
        }

        break
      }
      default:
        break
    }
  }

export default middleware
